/**
 *
 * KronoMetro
 *
 * Copyright © 2019-present KronoMetro, Co. All rights reserved.
 *
 */
"use client";

import * as React from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import { alpha } from "@mui/material/styles";
import ErrorIcon from "@mui/icons-material/ErrorRounded";
import WarningIcon from "@mui/icons-material/WarningRounded";
import InfoIcon from "@mui/icons-material/InfoRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircleRounded";
import CancelIcon from "@mui/icons-material/ClearRounded";

Alert.propTypes = {
  message: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  severity: PropTypes.string,
  onResolve: PropTypes.func,
};

function Alert({ ...props }) {
  const { isOpen, title, message, severity = "info", onResolve } = props;

  //console.log(`Alert props: ${JSON.stringify(props)}`);
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(true);
  const timer = React.useRef();

  React.useEffect(() => {
    if (isOpen) {
      clearTimeout(timer.current);
      setSuccess(false);
      setLoading(true);
      timer.current = setTimeout(() => {
        setSuccess(true);
        setLoading(false);
      }, 1200);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // severityに応じたアイコンと色を取得する関数
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case "error":
        return { icon: ErrorIcon, color: "error" };
      case "warning":
        return { icon: WarningIcon, color: "warning" };
      case "info":
        return { icon: InfoIcon, color: "info" };
      case "success":
        return { icon: CheckCircleIcon, color: "success" };
      default:
        return { icon: InfoIcon, color: "info" };
    }
  };

  const { icon: SeverityIcon, color: myColor } = getSeverityConfig(severity);

  const handleClose = () => {
    if (onResolve) {
      onResolve(true);
    }
  };

  return (
    <Dialog open={isOpen} fullWidth={true} maxWidth="xs" aria-labelledby="alert-dialog-title">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: (theme) => {
            switch (myColor) {
              case "error":
                return alpha(theme.palette.error.main, 0.1);
              case "warning":
                return alpha(theme.palette.warning.main, 0.1);
              case "info":
                return alpha(theme.palette.info.main, 0.1);
              case "success":
                return alpha(theme.palette.success.main, 0.1);
              default:
                return alpha(theme.palette.info.main, 0.1);
            }
          },
          color: (theme) => {
            switch (myColor) {
              case "error":
                return theme.palette.error.main;
              case "warning":
                return theme.palette.warning.main;
              case "info":
                return theme.palette.info.main;
              case "success":
                return theme.palette.success.main;
              default:
                return theme.palette.info.main;
            }
          },
        }}
      >
        <SeverityIcon
          sx={{
            fontSize: 40,
            color: (theme) => {
              switch (myColor) {
                case "error":
                  return theme.palette.error.main;
                case "warning":
                  return theme.palette.warning.main;
                case "info":
                  return theme.palette.info.main;
                case "success":
                  return theme.palette.success.main;
                default:
                  return theme.palette.info.main;
              }
            },
          }}
        />
        {title}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FormGroup
            row
            sx={{
              margin: 1,
              position: "relative",
            }}
          >
            <Box
              sx={{
                marginTop: "16px",
                fontSize: "20px",
                textAlign: "center",
                width: "100%",
              }}
            >
              {/*
               * 2026-09-30 r_mas
               * メッセージ中の改行を表示する。
               */}
              <div style={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: message }}></div>
            </Box>
          </FormGroup>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          margin: "0 auto",
        }}
      >
        <Button variant="contained" color="primary" onClick={handleClose} autoFocus>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Alert;
